import type React from 'react';

import { Link, Modal, ModalBody, ModalHeader, Separator, Text } from '@change/ds-core-ui';
import { LockIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core/react/i18n';
import { TrackingContext } from '@change-corgi/core/react/tracking';

import { LoginModalForm } from './components/LoginModalForm';
import { LoginModalSocialAuth } from './components/LoginModalSocialAuth';
import { useLoginModal } from './hooks/useLoginModal';
import { loginModalTrackingPropertyReset } from './loginModalTrackingPropertyReset';

export const LoginModal: React.FC = () => {
	const {
		data: {
			display,
			showContinueAsGuest,
			googleAuthEnabled,
			user,
			email,
			ariaLabel,
			title,
			separatorLabel,
			privacyNotice,
		},
		actions: { handleSuccess, closeModal, continueAsGuest },
	} = useLoginModal();

	if (!display) return null;

	return (
		<TrackingContext properties={loginModalTrackingPropertyReset} defaultOverrides={{ current_source: undefined }}>
			<Modal
				isDismissable
				isOpen
				onOpenChange={(open) => {
					if (!open) closeModal();
				}}
				aria-label={ariaLabel}
				onClose={closeModal}
				className="z-999 [&_h2]:w-full [&_h2]:max-w-none! [&_h2]:justify-center"
				size="small"
			>
				<ModalHeader title={title} />
				<ModalBody className="gap-0">
					<div className="mx-auto w-full max-w-[400px]">
						{showContinueAsGuest && (
							<Text as="div" size="small" className="mt-4 mb-6 text-center">
								<span className="bg-fill-warning-weak mr-2 inline-flex h-7 w-7 translate-y-0.5 items-center justify-center rounded-full">
									<LockIcon size="x-small" className="text-icon-warning" />
								</span>
								<Translate value="fe.components.login_or_join.saved_payment_info" />
							</Text>
						)}
						<LoginModalSocialAuth onSuccess={handleSuccess} googleAuthEnabled={googleAuthEnabled} />
						<Separator label={separatorLabel} className="my-9" />
						<LoginModalForm user={user} email={email} onSuccess={handleSuccess} />
						{showContinueAsGuest && (
							<div className="my-4 text-center">
								<Link bold as="button" onClick={() => continueAsGuest()} data-qa="guest-login-button">
									<Translate value="fe.pages.sponsors.guest_login" />
								</Link>
							</div>
						)}
						<Text as="p" size="small" className="text-typography-strong mt-8">
							{privacyNotice}
						</Text>
					</div>
				</ModalBody>
			</Modal>
		</TrackingContext>
	);
};
